import React from 'react';

// Composant Skeleton de base avec animation pulse
export const Skeleton = ({ className = '', ...props }) => {
  return (
    <div
      className={`animate-pulse bg-gray-200 rounded ${className}`}
      {...props}
    />
  );
};

// Skeleton pour une carte statistique
export const StatCardSkeleton = () => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
    <Skeleton className="h-4 w-20 mb-2" />
    <Skeleton className="h-8 w-16 mb-1" />
    <Skeleton className="h-3 w-24" />
  </div>
);

// Skeleton pour une mission dans le planning
export const MissionCardSkeleton = () => (
  <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 mb-2">
    <div className="flex items-center gap-2 mb-2">
      <Skeleton className="h-8 w-8 rounded-full" />
      <div className="flex-1">
        <Skeleton className="h-4 w-32 mb-1" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
    <Skeleton className="h-3 w-full mb-1" />
    <Skeleton className="h-3 w-3/4" />
  </div>
);

// Skeleton pour le planning (grille de jours)
export const PlanningGridSkeleton = ({ days = 7 }) => (
  <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
    {Array.from({ length: days }).map((_, dayIndex) => (
      <div key={dayIndex} className="bg-gray-50 rounded-lg p-3 min-h-[200px]">
        <Skeleton className="h-5 w-24 mb-3" />
        {Array.from({ length: 3 }).map((_, missionIndex) => (
          <MissionCardSkeleton key={missionIndex} />
        ))}
      </div>
    ))}
  </div>
);

// Skeleton pour une ligne de tableau
export const TableRowSkeleton = ({ columns = 5 }) => (
  <tr className="border-b border-gray-100">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <Skeleton className="h-4 w-full" />
      </td>
    ))}
  </tr>
);

// Skeleton pour un tableau complet
export const TableSkeleton = ({ rows = 5, columns = 5 }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="px-4 py-3 text-left">
                <Skeleton className="h-4 w-20" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <TableRowSkeleton key={i} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Skeleton pour les cartes du Dashboard
export const DashboardSkeleton = () => (
  <div className="space-y-6">
    {/* Statistiques */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <StatCardSkeleton key={i} />
      ))}
    </div>
    
    {/* Alertes */}
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <Skeleton className="h-5 w-32 mb-4" />
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="flex-1">
              <Skeleton className="h-4 w-48 mb-1" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Skeleton pour le Bilan
export const BilanSkeleton = () => (
  <div className="space-y-4">
    {/* Cartes CA par gestion */}
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <Skeleton className="h-10 w-full" />
      <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-gray-100 rounded-lg p-4">
            <Skeleton className="h-3 w-24 mb-2" />
            <Skeleton className="h-7 w-20 mb-1" />
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>
    </div>
    
    {/* Cartes métriques */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="bg-gray-100 rounded-xl p-5">
          <Skeleton className="h-4 w-40 mb-2" />
          <Skeleton className="h-10 w-24 mb-1" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </div>
    
    {/* Tableau employés */}
    <TableSkeleton rows={5} columns={6} />
  </div>
);

// Skeleton pour la Facturation
export const FacturationSkeleton = () => (
  <div className="space-y-4">
    {/* Header avec totaux */}
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-8 w-32" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i}>
            <Skeleton className="h-3 w-20 mb-1" />
            <Skeleton className="h-6 w-24" />
          </div>
        ))}
      </div>
    </div>
    
    {/* Liste clients */}
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div>
              <Skeleton className="h-5 w-32 mb-1" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          <Skeleton className="h-20 w-full rounded-lg" />
        </div>
      ))}
    </div>
  </div>
);

// Skeleton pour le Planning Calendar
export const PlanningCalendarSkeleton = () => (
  <div className="space-y-4">
    {/* Header navigation */}
    <div className="flex items-center justify-between mb-4">
      <Skeleton className="h-8 w-8 rounded" />
      <Skeleton className="h-6 w-40" />
      <Skeleton className="h-8 w-8 rounded" />
    </div>
    
    {/* Jours de la semaine */}
    <div className="grid grid-cols-7 gap-1 mb-2">
      {Array.from({ length: 7 }).map((_, i) => (
        <Skeleton key={i} className="h-8 rounded" />
      ))}
    </div>
    
    {/* Grille du calendrier */}
    <div className="grid grid-cols-7 gap-1">
      {Array.from({ length: 35 }).map((_, i) => (
        <div key={i} className="bg-gray-50 rounded-lg p-2 min-h-[100px]">
          <Skeleton className="h-5 w-6 mb-2" />
          <Skeleton className="h-6 w-full mb-1 rounded" />
          <Skeleton className="h-6 w-3/4 rounded" />
        </div>
      ))}
    </div>
  </div>
);

export default Skeleton;
